import { IPhoneNumber } from '@color/lib';

import { PopulationSettings } from 'ui/common/types';
import { Purchase } from 'ui/Purchase/Order/types';
import { Claim, Sku } from 'ui/Purchase/types';

import { LocationDetails } from './paths';
import { IGeneticsProvider } from './PhysicianTesting/types';

export enum GeneticsTestType {
  WISDOM_9 = 'wisdom 9',
  CARDIO_30 = 'cardio 30',
  PGX_V1 = 'pgx v1',
  PGX_V3 = 'pgx v3',
  HEREDITARY_30 = 'hereditary 30',
  FH_3 = 'fh 3',
  FH_5 = 'fh 5',
  BRCA_1_2 = 'brca 1 2',
  BREAST_OVARIAN_19 = 'breast ovarian 19',
  HEREDITARY_7 = 'hereditary 7',
  HEREDITARY_25 = 'hereditary 25',
  WHOLE_EXOME = 'whole exome',
  HEREDITARY_59 = 'hdr 59',
}

export enum GeneticsSkuName {
  COMBO_1 = Sku.COMBO_1,
  COMBO_2 = Sku.COMBO_2,
  COMBO_3 = Sku.COMBO_3,
  COMBO_3_V2 = Sku.COMBO_3_V2,
  CORE_COMBO = Sku.CORE_COMBO,
  CORE_COMBO_V2 = Sku.CORE_COMBO_V2,
  CORE_10 = Sku.CORE_10,
  BRCA_1_2 = Sku.BRCA_1_2,
  BREAST_OVARIAN_19 = Sku.BREAST_OVARIAN_19,
  CARDIO_30 = Sku.CARDIO_30,
  FH_3 = Sku.FH_3,
  HEREDITARY_30 = Sku.HEREDITARY_30,
  WISDOM_9 = Sku.WISDOM_9,
  HEREDITARY_59 = Sku.HEREDITARY_59,
}

export interface GeneticsSku {
  name: GeneticsSkuName;
  price_cents: number;
  provider_service_price_cents: number;
}

export const SkuToGeneticsTestTypes: Record<GeneticsSkuName, GeneticsTestType[]> = {
  [GeneticsSkuName.COMBO_1]: [GeneticsTestType.HEREDITARY_30, GeneticsTestType.FH_3],
  [GeneticsSkuName.COMBO_2]: [GeneticsTestType.HEREDITARY_30, GeneticsTestType.CARDIO_30],
  [GeneticsSkuName.COMBO_3]: [
    GeneticsTestType.HEREDITARY_30,
    GeneticsTestType.CARDIO_30,
    GeneticsTestType.PGX_V1,
  ],
  [GeneticsSkuName.COMBO_3_V2]: [
    GeneticsTestType.HEREDITARY_30,
    GeneticsTestType.CARDIO_30,
    GeneticsTestType.PGX_V3,
  ],
  [GeneticsSkuName.CORE_COMBO]: [
    GeneticsTestType.FH_3,
    GeneticsTestType.HEREDITARY_7,
    GeneticsTestType.PGX_V1,
  ],
  [GeneticsSkuName.CORE_COMBO_V2]: [
    GeneticsTestType.FH_3,
    GeneticsTestType.HEREDITARY_7,
    GeneticsTestType.PGX_V3,
  ],
  [GeneticsSkuName.CORE_10]: [GeneticsTestType.HEREDITARY_7, GeneticsTestType.FH_3],
  [GeneticsSkuName.BRCA_1_2]: [GeneticsTestType.BRCA_1_2],
  [GeneticsSkuName.BREAST_OVARIAN_19]: [GeneticsTestType.BREAST_OVARIAN_19],
  [GeneticsSkuName.CARDIO_30]: [GeneticsTestType.CARDIO_30],
  [GeneticsSkuName.FH_3]: [GeneticsTestType.FH_3],
  [GeneticsSkuName.HEREDITARY_30]: [GeneticsTestType.HEREDITARY_30],
  [GeneticsSkuName.WISDOM_9]: [GeneticsTestType.WISDOM_9],
  [GeneticsSkuName.HEREDITARY_59]: [GeneticsTestType.HEREDITARY_59],
};

export const TEST_TYPE_TO_DISPLAY_NAME: Record<GeneticsTestType, string> = {
  [GeneticsTestType.HEREDITARY_30]: 'Hereditary Cancer Test',
  [GeneticsTestType.FH_3]: 'Hereditary High Cholesterol Test',
  [GeneticsTestType.CARDIO_30]: 'Hereditary Heart Health Test',
  [GeneticsTestType.PGX_V1]: 'Medication Response Genetic Test',
  [GeneticsTestType.PGX_V3]: 'Medication Response Genetic Test',
  [GeneticsTestType.HEREDITARY_7]: 'BRCA and Lynch Test',
  [GeneticsTestType.BRCA_1_2]: 'BRCA Test',
  [GeneticsTestType.BREAST_OVARIAN_19]: 'Breast & Ovarian Cancer Test',
  [GeneticsTestType.WISDOM_9]: 'Wisdom Test',
  [GeneticsTestType.WHOLE_EXOME]: 'Color Test',
  [GeneticsTestType.HEREDITARY_25]: 'Color Test',
  [GeneticsTestType.FH_5]: 'Color Test',
  [GeneticsTestType.HEREDITARY_59]: 'Color Hereditary Disease Risk Test 2.0',
};

export enum PhysicianOrderState {
  NEW_STATE = 'new',
  CLAIMABLE_STATE = 'claimable',
  CLAIMED_STATE = 'claimed',
  CANCELLED_STATE = 'cancelled',
}

export enum PhysicianOrderType {
  SAMPLE_ORDERED = 'sample_ordered',
  SAMPLE_TAKEN_HOME = 'sample_taken_home',
  SAMPLE_AT_OFFICE = 'sample_at_office',
}

interface IOrderingPhysicianProfile {
  state: string;
  country: string;
}

export interface IPhysicianOrder {
  id: number;
  requisition_number: string;
  state: PhysicianOrderState;
  type: PhysicianOrderType;
  test_requested: GeneticsSkuName;
  ordering_physician_profile?: IOrderingPhysicianProfile;
  // TODO: Investigate claim_for_payment and promo_code_for_payment if
  // that needs to be stored in context.
}

export interface IActivationFlowInfo {
  orderNumbers: string[];
  test_types: GeneticsTestType[];
  sku: GeneticsSkuName | undefined;
  population_settings: PopulationSettings | null;
  order_numbers: string[];
  requirePayment: boolean;
  requireOrderingPhysician: boolean;
  isClaimable: boolean;
  hasPhysicianOrder: boolean;
  hasActivated: boolean;
  kioPhysicianOrder: IPhysicianOrder | null;
}

export enum InformedConsentType {
  COLOR = 'color',
  WISDOM = 'wisdom',
}

export interface IRegistrationInfo {
  gender_identity?: string;
  // Equivalent to sex_assigned_at_birth
  gender: string;
  birthday: string;
  consent_type: InformedConsentType;
  can_use_for_research: boolean;
  allowed_to_store: boolean;
  phone_number: IPhoneNumber;
  accession_number: string;
  kit_barcode: string;
  heard_about_color_through: string;
  collection_location: string;
  collection_zip_code: string | null;
  additional_consent_type?: string;
  has_consented: boolean;
}

export interface IAuthorizationInfo {
  agreed_to_hipaa_authorization: boolean | undefined;
}

interface IGeneticsItem {
  quantity: number;
  sku: GeneticsSkuName;
}

export interface IGeneticsPurchase extends Omit<Purchase, 'items' | 'promotional_code'> {
  shared_providers?: IGeneticsProvider[];
  /*
   * We use the actual PromotionalCode.code instead
   * of the ID here. Therefore we the omit + redefine allows
   * us to type change.
   */
  promotional_code?: string;
  /*
   * GeneticsSkuName is a subset of Sku from lib/constants.
   * However, our normal purchase object expects the extended Sku.
   */
  items?: IGeneticsItem[];
  sku: GeneticsSkuName | undefined;
  kit_barcode: string;
  accession_number: string;
}

export interface IKitCheckoutInfo {
  purchase: IGeneticsPurchase;
  physician_order: IPhysicianOrder | null;
  colorOrderingPhysicianIsAdded: boolean;
  sharedProvidersIsEnabled: boolean;
}

export interface IPromotionalCode {
  code: string;
  campaign: {
    company_name: string;
  } | null;
  can_redeem: boolean;
  percent_discount: number;
  value_cents: number;
  sku_list: GeneticsSkuName[];
  custom_kit_price_cents: number | null;
  is_family_testing_code: boolean;
  remaining_redemptions_count: number;
  // TODO: Add fields as they become necessary.
}

export interface ISample {
  kit_barcode: string;
  accession_number: string;
}

interface ISampleStatus extends ISample {
  activated_at: string;
}

export interface IKitStatus {
  sample?: ISampleStatus;
  test_type?: GeneticsTestType;
  sku?: GeneticsSkuName;
  order_status: string;
  physician_order: IPhysicianOrder | undefined;
  order_number: string;
}

export interface IKitOrder {
  order_number: string;
  test_type: GeneticsTestType;
}

export interface IVerifyActivationAPIResponse {
  kit_orders: IKitOrder[];
  physician_order: IPhysicianOrder | null;
  promo_code: IPromotionalCode | null;
  claim: Claim | null;
  is_claimable_sample: boolean;
  test_types: GeneticsTestType[];
  population_settings: PopulationSettings | null;
  is_order_info_finalized: boolean;
  purchased_by_current_user: boolean;
  locations: LocationDetails[];
  user_matched_physician_order: IPhysicianOrder | null;
  purchase: {
    is_multi_purchase: boolean;
    sku: GeneticsSkuName;
    ordering_option_chosen: boolean;
    promo_code?: IPromotionalCode;
    physician_order?: IPhysicianOrder;
    shared_providers?: IGeneticsProvider[];
  };
}
